import { Button, EmptyState, Separator, Tabs, TabsList, TabsTrigger } from '@aibi/ui';
import { ArrowLeft, ChartNoAxesCombined, LogOut, ServerOff } from 'lucide-react';
import type { ReactNode } from 'react';
import { useServices } from '../app/services';
import { useUiDispatch, type AppView } from '../workspace/state/ui-state';
import { AdminGate } from './components/AdminGate';
import { AiSettings } from './components/AiSettings';
import { ConnectionsCenter } from './components/ConnectionsCenter';
import { useAdminSession } from './state/admin-session';
import { AdminSessionProvider } from './state/AdminSessionProvider';

export type SettingsSection = Exclude<AppView, 'analysis'>;

/**
 * Configuracion de la app: conexiones de datos y asistente de IA. Todo lo que muestra
 * pasa por `/api/admin/*`, que el servidor protege con la sesion de administrador.
 */
export function SettingsView({ section }: { readonly section: SettingsSection }) {
  const { admin } = useServices();
  const uiDispatch = useUiDispatch();

  if (admin === undefined) {
    return (
      <SettingsFrame section={section}>
        <EmptyState
          icon={<ServerOff />}
          title="Configuracion no disponible"
          description="La configuracion de conexiones y del asistente requiere el servidor. En la version de escritorio aun no esta disponible."
          action={
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                uiDispatch({ type: 'set-active-view', view: 'analysis' });
              }}
            >
              Volver al analisis
            </Button>
          }
        />
      </SettingsFrame>
    );
  }

  return (
    <AdminSessionProvider services={admin}>
      <SettingsFrame section={section} withSession>
        <AdminGate>{section === 'connections' ? <ConnectionsCenter /> : <AiSettings />}</AdminGate>
      </SettingsFrame>
    </AdminSessionProvider>
  );
}

function SettingsFrame({
  section,
  withSession = false,
  children,
}: {
  readonly section: SettingsSection;
  readonly withSession?: boolean;
  readonly children: ReactNode;
}) {
  const uiDispatch = useUiDispatch();
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="flex h-12 shrink-0 items-center gap-2 border-b border-border bg-surface px-3">
        <span className="flex size-7 items-center justify-center rounded-md bg-primary text-primary-foreground">
          <ChartNoAxesCombined aria-hidden className="size-4" />
        </span>
        <h1 className="text-sm font-semibold">Configuracion</h1>
        <Separator orientation="vertical" className="mx-1 h-6" />
        <Tabs
          value={section}
          onValueChange={(value) => {
            uiDispatch({ type: 'set-active-view', view: value as SettingsSection });
          }}
        >
          <TabsList>
            <TabsTrigger value="connections">Conexiones de datos</TabsTrigger>
            <TabsTrigger value="assistant">Asistente de IA</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="ml-auto flex items-center gap-1">
          {withSession && <LogoutButton />}
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              uiDispatch({ type: 'set-active-view', view: 'analysis' });
            }}
          >
            <ArrowLeft /> Volver al analisis
          </Button>
        </div>
      </header>
      <main className="flex min-h-0 flex-1 flex-col overflow-auto">{children}</main>
    </div>
  );
}

function LogoutButton() {
  const session = useAdminSession();
  if (session.status.phase !== 'ready') return null;
  return (
    <Button size="sm" variant="ghost" onClick={() => void session.logout()}>
      <LogOut /> Cerrar sesion
    </Button>
  );
}
